import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { 
  Shield, Building2, Users, CreditCard, Package, HelpCircle, 
  Mail, History, FileText, LogOut, Menu, X, Cloud, Database,
  AlertTriangle, Bell, Settings, BarChart3, Archive, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminLayout = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile menu
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('movemate_superadmin_sidebar_collapsed');
    return saved === 'true';
  });
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [urgentTicketsCount, setUrgentTicketsCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  
  const token = localStorage.getItem('movemate_token');

  // Save sidebar collapsed preference
  useEffect(() => {
    localStorage.setItem('movemate_superadmin_sidebar_collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Fetch counts for badges
  const fetchCounts = useCallback(async () => {
    try {
      // Fetch open tickets
      const openRes = await fetch(`${API_URL}/api/superadmin/tickets?status=open&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (openRes.ok) {
        const openData = await openRes.json();
        setOpenTicketsCount(openData.total || 0);
      }
      
      // Fetch urgent tickets
      const urgentRes = await fetch(`${API_URL}/api/superadmin/tickets?priority=urgent&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (urgentRes.ok) {
        const urgentData = await urgentRes.json();
        const urgentOpen = (urgentData.tickets || []).filter(t => t.status !== 'closed' && t.status !== 'resolved').length;
        setUrgentTicketsCount(urgentOpen);
      }
      
      // Fetch pending items
      const pendingRes = await fetch(`${API_URL}/api/superadmin/pending-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingCount((pendingData.items || []).length);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  }, [token]);

  useEffect(() => {
    let isMounted = true;
    
    const loadCounts = async () => {
      if (isMounted) {
        await fetchCounts();
      }
    };
    
    loadCounts();
    const interval = setInterval(loadCounts, 60000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchCounts]);

  const handleLogout = () => {
    localStorage.removeItem('movemate_token');
    localStorage.removeItem('movemate_user');
    setUser(null);
    navigate('/superadmin/login');
  };

  const menuItems = [
    { path: '/superadmin', icon: <BarChart3 size={20} />, label: 'Overview', exact: true },
    { path: '/superadmin/companies', icon: <Building2 size={20} />, label: 'Companies' },
    { path: '/superadmin/subscriptions', icon: <CreditCard size={20} />, label: 'Subscriptions' },
    { path: '/superadmin/admins', icon: <Users size={20} />, label: 'Admin Users' },
    { 
      path: '/superadmin/approvals', 
      icon: <Package size={20} />, 
      label: 'Approvals',
      badge: pendingCount > 0 ? pendingCount : null
    },
    { 
      path: '/superadmin/tickets', 
      icon: <HelpCircle size={20} />, 
      label: 'Support Tickets',
      badge: openTicketsCount > 0 ? openTicketsCount : null,
      urgent: urgentTicketsCount > 0
    },
    { path: '/superadmin/messaging', icon: <Mail size={20} />, label: 'Messaging' },
    { path: '/superadmin/storage', icon: <Cloud size={20} />, label: 'Storage & Backups' },
    { path: '/superadmin/maintenance', icon: <Settings size={20} />, label: 'Maintenance Mode' },
    { path: '/superadmin/activity', icon: <History size={20} />, label: 'Activity' },
    { path: '/superadmin/audit', icon: <Shield size={20} />, label: 'Audit Log' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <TooltipProvider delayDuration={100}>
      <div className="min-h-screen bg-gray-100 flex" data-testid="superadmin-panel">
        {/* Sidebar */}
        <aside className={`bg-slate-900 text-white ${sidebarWidth} fixed h-full z-30 transition-all duration-300 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {/* Header */}
          <div className="p-4 border-b border-slate-700 flex-shrink-0">
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              <Shield className="h-10 w-10 text-amber-400 flex-shrink-0" />
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-xl font-bold text-white">MoveMate</h1>
                  <p className="text-xs text-slate-400">Superadmin Panel</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
            {menuItems.map((item) => {
              const button = (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-amber-500 text-slate-900'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-3'}`}>
                    <div className="relative">
                      {item.icon}
                      {/* Badge indicator for collapsed state */}
                      {sidebarCollapsed && item.badge && (
                        <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${item.urgent ? 'bg-red-500' : 'bg-amber-400'}`} />
                      )}
                    </div>
                    {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
                  </div>
                  {!sidebarCollapsed && item.badge && (
                    <Badge 
                      variant={item.urgent ? "destructive" : "secondary"} 
                      className={`ml-auto ${item.urgent ? 'bg-red-500' : 'bg-slate-600'}`}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </button>
              );

              // Wrap in tooltip when collapsed
              if (sidebarCollapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      {button}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                      <div className="flex items-center gap-2">
                        {item.label}
                        {item.badge && (
                          <Badge 
                            variant={item.urgent ? "destructive" : "secondary"} 
                            className={item.urgent ? 'bg-red-500' : 'bg-slate-600'}
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-3 border-t border-slate-700 flex-shrink-0">
            {!sidebarCollapsed && (
              <div className="mb-3 px-2">
                <p className="text-sm font-semibold text-white truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                <Badge variant="outline" className="mt-1 text-amber-400 border-amber-400">
                  Super Admin
                </Badge>
              </div>
            )}
            
            {sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                  Logout
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            )}
          </div>

          {/* Collapse Toggle Button - Desktop only - In header */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex items-center justify-center absolute top-4 right-2 w-8 h-8 bg-slate-700 hover:bg-slate-600 border border-slate-500 rounded-lg transition-colors z-50"
            data-testid="sidebar-collapse-toggle"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <ChevronRight size={18} className="text-slate-200" />
            ) : (
              <ChevronLeft size={18} className="text-slate-200" />
            )}
          </button>
        </aside>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-4 left-4 z-40 bg-slate-900 text-white p-2 rounded-lg shadow-lg"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className={`flex-1 ${mainMargin} transition-all duration-300`}>
          {/* Top notification bar for urgent items */}
          {urgentTicketsCount > 0 && (
            <div className="bg-red-500 text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} />
                <span className="text-sm font-medium">
                  {urgentTicketsCount} urgent ticket{urgentTicketsCount !== 1 ? 's' : ''} require attention
                </span>
              </div>
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => navigate('/superadmin/tickets')}
              >
                View Tickets
              </Button>
            </div>
          )}
          
          {/* Page Content */}
          <div className="p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
};

export default SuperAdminLayout;
